import EverTrue from 'app';
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Loading, ModalTrigger, Icon, useOpenClose } from '@evertrue/et-components';
import Api from 'entities/helpers/api';
import { MultiRecipientsInput } from './multi-recipients-input';
import SubjectInput from './subject-input';
import EmailTemplatesModalController from 'apps/outreach/email/templates/email-templates-modal-controller';
import EmailSignaturesModal from 'apps/outreach/email/signatures/components/email-signatures-modal';
import ThankviewCTA from '../../thankview/thankview-recorder-cta';
import FileUpload from '../components/file-upload';
import UploadedFiles from '../components/uploaded-files';
import useFileUploader from '../hooks/use-file-uploader';
import useDynamicFields from '../hooks/use-dynamic-fields';
import useFormatEmailBody from '../hooks/use-format-email-body';
import MissingInfoModal from 'components/emailer/components/missing-information-modal';
import SplitButton from '../email-send-split-button.js';
import { useEmailSignaturesProvider } from 'apps/outreach/email/signatures/email-signatures-context';
import { Formik } from 'formik';
import { emailSchema, setDefaultEmailValues, buildEmailPayload } from './email-schema';
import { RichTextEditor, debounce, isEmpty } from '@evertrue/sdk';
import { useEmailDrafts } from 'apps/outreach/email/drafts/email-drafts-context';
import { useScheduledEmails } from 'apps/outreach/email/scheduled/scheduled-email-context';
import { parseThankviewLink } from './parse-thankview-link';

export const EmailForm = ({
  contact,
  email,
  taskId,
  closeDrawer = () => {},
  handleClose = () => {},
  setDrawerTitle = () => {},
  drawerId,
  provider,
  clickedEmailAddress = '',
}) => {
  const joditRef = useRef(null);

  const [signaturesModalIsOpen, openSignaturesModal, closeSignaturesModal] = useOpenClose(false);
  const { defaultSignature, loading: signatureLoading } = useEmailSignaturesProvider();
  const { getFormattedSignature, hasSignatureOnly, parseBodyForInteraction } = useFormatEmailBody();
  const { files, setFiles, removeFile, onFileUpload, onImageUpload, isReuploading, refreshInlineImageUrls } =
    useFileUploader(email?.email_attachments || [], drawerId);

  const { dynamicFields, getUnsupportedFields, bodyHasMissingFields, getEmptyFields } = useDynamicFields(contact);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [originalSendType, setOriginalSendType] = useState('send');
  const [isImageUploading, setIsImageUploading] = useState(false);

  const formikRef = useRef(null);
  const hasRefreshedBodyRef = useRef(false);

  const { createScheduled } = useScheduledEmails();
  const { createDraft, updateDraft, saveDraftToSessionStorage, removeDraftFromSessionStorage, deleteDraftById } =
    useEmailDrafts();

  const defaultEmailValues = setDefaultEmailValues(
    email,
    contact,
    defaultSignature,
    getFormattedSignature,
    clickedEmailAddress,
    taskId
  );

  // Refresh expired S3 signed URLs once the initial body is populated.
  // Runs for drafts (body present on mount) and for new emails after the
  // default signature loads async — the original [] dep fired too early
  // for signatures and left stale URLs in the editor.
  useEffect(() => {
    if (hasRefreshedBodyRef.current || !defaultEmailValues.body) return;
    hasRefreshedBodyRef.current = true;
    const refreshBodyUrls = async () => {
      const refreshedBody = await refreshInlineImageUrls(defaultEmailValues.body);
      if (refreshedBody === defaultEmailValues.body) return;
      formikRef.current?.setFieldValue('body', refreshedBody);
      const editorEl = document.getElementsByClassName('jodit-wysiwyg')[0];
      if (editorEl) editorEl.innerHTML = refreshedBody;
    };
    refreshBodyUrls();
  }, [defaultEmailValues.body, refreshInlineImageUrls]);

  const cleanupFilesFromSessionStorage = () => {
    const filesKey = `${drawerId}_files`;
    sessionStorage.removeItem(filesKey);
  };

  const handleSubmit = async (values, type = 'send', bypassFields = false) => {
    if (bodyHasMissingFields(values.body)) {
      const fieldsNotSupported = getUnsupportedFields(values.body);
      const emptyFields = getEmptyFields(values.body);
      const foundMissingFields = [...fieldsNotSupported, ...emptyFields];
      if (foundMissingFields?.length > 0 && !bypassFields) {
        setOriginalSendType(type);
        setMissingFields(foundMissingFields);
        setShowReviewPopup(true);
        return;
      }
    }
    // Refresh any expired S3 signed URLs for inline images before sending
    values.body = await refreshInlineImageUrls(values.body);

    const { payload } = buildEmailPayload(values, type, files);
    if (type === 'send') {
      try {
        await sendEmail(payload);
      } catch (error) {
        // Preserve the email as a new draft on send failure so the user doesn't lose their work.
        // Keep the original draft intact here — deleting it would race with the new createDraft.
        try {
          await createDraft(payload, drawerId, { disableAlerts: true });
          EverTrue.Alert.error('Failed to send email. Your email has been saved as a draft.');
        } catch (draftError) {
          EverTrue.Alert.error('Failed to send email and could not save it as a draft. Please try again.');
        }
        removeDraftFromSessionStorage(drawerId);
        cleanupFilesFromSessionStorage();
        closeDrawer();
        return;
      }
    } else if (type === 'scheduled') {
      await createScheduled(payload);
    }
    handleDeleteEmail(values, drawerId, true);

    setShowReviewPopup(false);
    setMissingFields([]);

    removeDraftFromSessionStorage(drawerId);
    cleanupFilesFromSessionStorage();

    let interactionBody = parseBodyForInteraction(values.body);

    interactionBody = parseThankviewLink(values.body, interactionBody);

    // current recipient email, taking from the "to" field in the drawer
    const recipientEmail = values.to[0];

    // find matching contact in email_recipients (regardless of TO or FROM)
    const maybeContact = email?.email_recipients?.find((recipient) => recipient.email_address === recipientEmail);

    if (!isEmpty(contact)) {
      handleClose({
        contact,
        interaction: {
          summary: values.subject,
          text: `Sent the following email:<br>${interactionBody}`,
        },
      });
    } else if (!isEmpty(maybeContact?.contact)) {
      handleClose({
        contact: maybeContact?.contact,
        interaction: {
          summary: values.subject,
          text: `Sent the following email:<br>${interactionBody}`,
        },
      });
    } else {
      closeDrawer();
    }
  };

  const sendEmail = async (payload) => {
    await Api.OUTREACH.SEND.post({
      data: JSON.stringify(payload),
      success_message: 'Email Sent Successfully',
      disableAlerts: true,
    });
    EverTrue.track.set('outreach_action', { type: 'send_email' });
  };

  const handleDeleteEmail = async (values, drawerId, hideAlert = false) => {
    if (values.nylas_draft_id) {
      await removeDraftFromSessionStorage(drawerId);
      await deleteDraftById(values.id, drawerId, hideAlert);
    } else {
      removeDraftFromSessionStorage(drawerId);
    }

    cleanupFilesFromSessionStorage();
    closeDrawer();
  };

  const handleImageUpload = async (...args) => {
    setIsImageUploading(true);
    try {
      return await onImageUpload(...args);
    } catch (error) {
      // Re-throw the error so the RichTextEditor can handle it properly
      throw error;
    } finally {
      setIsImageUploading(false);
    }
  };

  // Save draft to api when drawer is closed
  useEffect(() => {
    return async () => {
      if (sessionStorage.getItem(drawerId)) {
        const savedDraft = JSON.parse(sessionStorage.getItem(drawerId));
        const shouldSaveOrUpdateDraft =
          savedDraft.subject || !hasSignatureOnly(savedDraft.body) || (savedDraft.to && savedDraft.to.length > 0);
        if (shouldSaveOrUpdateDraft) {
          // Get files from sessionStorage
          const filesKey = `${drawerId}_files`;
          const savedFiles = sessionStorage.getItem(filesKey);
          const currentFiles = savedFiles ? JSON.parse(savedFiles) : [];

          // Refresh any expired S3 signed URLs before saving draft
          savedDraft.body = await refreshInlineImageUrls(savedDraft.body);

          if (savedDraft.nylas_draft_id) {
            const { payload, id } = buildEmailPayload(savedDraft, 'update', currentFiles);
            await updateDraft(payload, id, drawerId);
          } else {
            const { payload } = buildEmailPayload(savedDraft, 'send', currentFiles);
            await createDraft(payload, drawerId);
          }

          // Clean up files from sessionStorage after saving
          cleanupFilesFromSessionStorage();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Catch default signature loading, don't update if modal is open
  if (signatureLoading && !signaturesModalIsOpen) return <Loading spinner_size="large" />;

  return (
    <Formik
      innerRef={formikRef}
      initialValues={defaultEmailValues}
      validationSchema={emailSchema}
      onSubmit={handleSubmit}
    >
      {({ handleChange, setFieldValue, values, errors, touched, isValid, isSubmitting }) => {
        debounce(saveDraftToSessionStorage, 500)(values, drawerId);
        return (
          <div className="email-form">
            <div className="email-form-header">
              <MultiRecipientsInput
                values={{
                  to: values.to || [],
                  cc: values.cc || [],
                  bcc: values.bcc || [],
                }}
                setFieldValue={setFieldValue}
              />
              <SubjectInput
                name="Subject"
                field="subject"
                value={values.subject}
                setFieldValue={setFieldValue}
                setDrawerTitle={setDrawerTitle}
              />
            </div>
            <div className="rte-container" style={{ position: 'relative' }}>
              <RichTextEditor
                ref={joditRef}
                value={values.body}
                onChange={handleChange('body')}
                dynamicFields={dynamicFields}
                emailProvider={provider}
                onImageUpload={handleImageUpload}
                onFileUpload={onFileUpload}
              />
              {isImageUploading && (
                <div className="rte-loading-overlay">
                  <Loading size="large" />
                </div>
              )}
            </div>
            {(files.some((file) => !file.isInline) || isReuploading) && (
              <div className="email-form--files" style={{ height: '78px', overflowY: 'auto', paddingTop: '10px' }}>
                {isReuploading && files.length === 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                    <Loading size={'small'} />
                    <span style={{ marginLeft: '10px' }}>Loading existing attachments...</span>
                  </div>
                )}
                <UploadedFiles files={files} setFiles={setFiles} removeFile={removeFile} />
              </div>
            )}
            <div className="email-form--footer space-between">
              <div className="space-between" style={{ width: '100%' }}>
                <div className="space-between">
                  <SplitButton
                    handleSubmit={handleSubmit}
                    values={values}
                    setFieldValue={setFieldValue}
                    disabled={!isValid || isSubmitting || !values.to.length}
                  />
                  <div className="email-footer-btns">
                    <FileUpload onFileChange={onFileUpload} allowMultiple={true} />
                    <Icon
                      icon="email-signature-icon"
                      className="email-signatures-btn"
                      iconKit="gt2"
                      onClick={openSignaturesModal}
                      isReachable={true}
                      onKeyUp={openSignaturesModal}
                    />
                    <ModalTrigger
                      size={'1060px'}
                      closeOnOutsideClick={true}
                      buttonProps={{ type: 'simple' }}
                      buttonText={<Icon icon="email-template" iconKit="gt2" className="email-signatures-btn" />}
                    >
                      {({ closeModal }) => (
                        <EmailTemplatesModalController
                          contact={contact}
                          closeModal={closeModal}
                          setFieldValue={setFieldValue}
                          emailBody={values.body}
                          refreshInlineImageUrls={refreshInlineImageUrls}
                        />
                      )}
                    </ModalTrigger>
                  </div>
                </div>
                <div>
                  <ThankviewCTA />
                  <Icon icon="trash" className="delete-email-btn" onClick={() => handleDeleteEmail(values, drawerId)} />
                </div>
              </div>
              <EmailSignaturesModal
                isOpen={signaturesModalIsOpen}
                onClose={closeSignaturesModal}
                setFieldValue={setFieldValue}
                emailBody={values.body}
                contact={contact}
              />
            </div>
            <MissingInfoModal
              title="Review Missing Information"
              secondaryButtonLabel={originalSendType === 'scheduled' ? 'Schedule Anyway' : 'Send Anyway'}
              missingFields={missingFields}
              handleSubmit={() => {
                handleSubmit(values, originalSendType, true);
              }}
              isOpen={showReviewPopup}
              close={() => setShowReviewPopup(false)}
            />
          </div>
        );
      }}
    </Formik>
  );
};

EmailForm.propTypes = {
  taskId: PropTypes.number,
  handleClose: PropTypes.func,
  setDrawerTitle: PropTypes.func,
  contact: PropTypes.object,
  email: PropTypes.object,
  closeDrawer: PropTypes.func,
  drawerId: PropTypes.string,
  provider: PropTypes.string,
  clickedEmailAddress: PropTypes.string,
};

export default EmailForm;
